(function () {
  "use strict";

  let isBulkHeadwear = window.isBulkHeadwear; // this was set in product-form.liquid
  let isBulkAccessory = window.isBulkAccessory; // this was set in product-form.liquid
  // -------------------------------------------------------------------------
  // CURRENCY CONVERSION & FORMATTING HELPERS
  // -------------------------------------------------------------------------
  // Converts a price (in cents) from the base currency into the active currency.
  // Assumes Shopify.currency.rate is available.
  function convertPrice(price) {
    // If the currency converter is available, convert using the active currency stored there.
    if (
      window.currencyConverter &&
      typeof window.Currency !== "undefined" &&
      window.Currency.convert
    ) {
      // Use Shopify.currency.base for the base currency and the currencyConverter’s currentCurrency for the active one.
      return window.Currency.convert(
        price,
        Shopify.currency.base,
        window.currencyConverter.currentCurrency
      );
    }
    return price;
  }

  // Fix the formatPrice function to handle missing currencyConverter
  function formatPrice(price) {
    try {
      // Use window.currencyConverter if available for proper formatting:
      if (window.currencyConverter?.options) {
        // Choose the proper format based on your displayFormat setting.
        const format =
          window.currencyConverter.options.displayFormat === "money_format"
            ? window.currencyConverter.options.moneyFormatNoCurrency
            : window.currencyConverter.options.moneyFormatCurrency;
        return formatMoney(price, format);
      } else if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
        // Fallback to Shopify's formatting
        const format =
          Shopify.currency &&
          Shopify.currency.active !== Shopify.currency.shop_currency
            ? Shopify.currency.presentment_money_format
            : Shopify.money_format;
        return Shopify.formatMoney(price, format);
      }
    } catch (e) {
      console.warn("Error in formatPrice:", e);
    }
    // Fallback: assume dollars, converting cents to dollars.
    return "$" + (price / 100).toFixed(2);
  }

  // Add this function after the formatPrice function
  function formatPriceWithoutTrailingZeros(price) {
    // First use the existing price formatter
    const formatted = formatPrice(price);

    // Then remove ".00" if it appears at the end
    return formatted.replace(/\.00(?!\d)/g, "");
  }

  // Normalizes a hat price value. If the price is less than 100, we assume it’s in dollars
  // and multiply by 100 to convert it to cents. This does not affect addon prices.
  function normalizeHatPrice(price) {
    return price < 100 ? price * 100 : price;
  }

  // -------------------------------------------------------------------------
  // CONFIGURATION & GLOBAL VARIABLES
  // -------------------------------------------------------------------------
  let defaultConfig = {
    // Core module settings
    showStrikethrough: true,
    enableModule: true,
    recreatePrimaryIfMissing: true,
    modifyOriginalPrimary: false,
    hideOriginalPrimary: true,
    primarySelector: "#est_node",
    hookIntoQBGrid: true,
    qbGridSelector: ".shappify_qb_grid",
    trackQuantityChanges: true,

    // Visibility control flags
    hideUntilQuantityThreshold: true, // When true, hides UI unless qty > 0
    hideWithoutAddons: true, // When true, hides UI when no addons are selected

    // Placement & positioning
    position: "bottom",
    placementOffset: "0px",

    // CSS variables for display container
    cssVariables: {
      backgroundColor: "#fff",
      borderTop: "1px solid #ccc",
      borderBottom: "none",
      borderLeft: "none",
      borderRight: "none",
      zIndex: "9999",
      padding: "10px",
      fontSize: "16px",
      color: "#333",
    },

    // Additional strings
    loadingText: "Loading estimate...",
    addonSpinnerText: "Retrieving extras...",

    // Auto-update integration
    autoUpdate: true,
    updateCallback: null,

    // Close button / auto hide options
    closeButton: false,
    closeButtonText: "×",
    autoHideOnScroll: false,

    // Extra custom content
    extraContent: "",

    // Explosion effect configuration
    explosionEffect: {
      enabled: true,
      mode: "mask",
      emojiImages: ["🎉", "🥳", "🎊"],
      emojiImagesBonus: ["🎉", "🥳", "🎊"],
      alternativeMaskImage: "",
      alternativeFontSize: "64px",
      colorFilters: [
        { color: "#000000", filter: "brightness(0) saturate(100%)" },
        { color: "#7e826d", filter: "brightness(100%) saturate(100%)" },
        { color: "#5b7c99", filter: "brightness(100%) saturate(100%)" },
      ],
      colorFiltersBonus: [
        { color: "#ff0000", filter: "brightness(100%) saturate(100%)" },
      ],
      count: 25,
      duration: 1000,
      gravity: 0.2,
      friction: 0.99,
      initialVelocity: { min: 5, max: 20 },
    },

    // Minigame-specific options
    minigameEnabled: false,
    minigameProgressBar: false,
    progressBarColors: {
      fillColor: "#7e826d",
      backgroundColor: "#ddd",
    },
    progressBarAnimation: "fade",
    progressBarHeight: "5px",

    // Animation duration for smooth transitions.
    animationDuration: 300,

    // Custom class names for display elements
    customClassNames: {
      primary: "est_node",
      secondary: "est_node_secondary",
    },

    // Addon-related configuration
    addonObserverEnabled: true,
    addonInfoDisplay: true,

    // Mask-Fill Effect Options
    maskEffectConfig: {
      maskImage:
        "https://deknicreations.com/cdn/shop/files/tear_drop_small_678cdc61-111c-43c2-b05a-45b9ab2e62b7.png",
      defaultFillColor: "#797979",
      progressFillColor: "#000000",
      outline: false,
      outlineColor: "#ffffff",
      outlineWidth: 2,
      bounce: true,
      pulse: false,
      scaleToContent: true,
      cropMask: true,
    },

    // Layout configuration for rows and columns
    layoutConfig: {
      topRow: {
        enabled: true,
        rowStyles: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        },
        columns: [],
      },
      middleRow: {
        enabled: true,
        rowStyles: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        },
        columns: [
          {
            id: "logo",
            enabled: true,
            width: "10%",
            align: "center",
            columnStyles: {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            },
          },
          {
            id: "textDetails",
            enabled: true,
            width: "50%",
            align: "center",
            columnStyles: {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            },
          },
          {
            id: "additionalInfo",
            enabled: true,
            width: "30%",
            align: "right",
            columnStyles: {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            },
          },
        ],
      },
      bottomRow: {
        enabled: true,
        rowStyles: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        },
        columns: [
          {
            id: "progressBar",
            enabled: true,
            width: "100%",
            align: "center",
            columnStyles: {},
          },
        ],
      },
    },

    // Text display configuration for various rows
    textDisplayConfig: {
      summaryRow: {
        enabled: true,
        style: {
          color: "#000",
          fontSize: "16px",
          fontFamily: "Arial, sans-serif",
          textAlign: "center",
        },
        qty: { enabled: true, prefix: "", suffix: " Units" },
        unitPrice: { enabled: true, prefix: "", suffix: " Each" },
        total: { enabled: true, prefix: "Total: ", suffix: "" },
      },
      progressRow: {
        enabled: true,
        style: { color: "#333", fontSize: "16px", textAlign: "center" },
        text: "Progress: {progress}%",
      },
      nextBreakpointRow: {
        enabled: true,
        style: {
          color: "#fff",
          fontSize: "14px",
          textAlign: "center",
          background: "#444",
          padding: "4px",
          marginBottom: "0px",
        },
        text: "{nextTierText}",
      },
      addonRows: {
        enabled: true,
        style: { color: "#333", fontSize: "14px", textAlign: "center" },
      },
      cartRow: {
        enabled: true,
        style: {
          color: "#fff",
          fontSize: "16px",
          textAlign: "center",
          background: "#333",
          padding: "6px",
        },
        template: `<div style="font-size: 16px; font-family: Oswald, sans-serif; text-align: center; margin-top: 0px; background-color: #333; color: #fff">
          {qty} items
        </div>
        <div style="font-size: 18px; font-weight: bold; font-family: Oswald, sans-serif; text-align: center; background-color: #333; color: #fff">
          ESTIMATED TOTAL: {total} {discountLine}
        </div>`,
      },
    },

    // Default styles for mask container (logo column)
    maskContainerStyles: {
      position: "relative",
      width: "100%",
      paddingTop: "100%",
      minHeight: "40px",
      minWidth: "40px",
      overflow: "hidden",
    },

    // Mode configuration
    mode: "full",

    // Flags for explosion clamping and chat detection
    clampExplosionParticles: true,
    detectChatButton: false,
  };

  let config = window.secondaryEstConfig
    ? Object.assign({}, defaultConfig, window.secondaryEstConfig)
    : defaultConfig;
  if (!config.enableModule) return;

  // Global tracking variables
  let previousProgress = 0;
  let lastBreakpointIndex = -1;
  let explosionOriginEl = null;
  let secondaryEstimatorEl = null;
  let previousAddonCount = 0;
  let previousAddonTotal = 0;
  let previousAddonText = ""; // Add this new variable to track addon text

  function addAnimationStyles() {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      @keyframes addonPulse {
        0% { transform: scale(1) color: inherit; text-shadow: none; }
        30% { transform: scale(0.8) color:rgb(255, 255, 255); }
        50% { transform: scale(1.05) color:rgb(255, 255, 255);  }
        70% { transform: scale(1.07) color:rgb(255, 255, 255);  }
        100% { transform: scale(1) color: inherit; text-shadow: none; }
      }
      /* Increased specificity */
      #est_secondary_close .addon-pulse,
      #est_secondary .addon-pulse {
        display: inline-block !important;
        animation: addonPulse 1.2s ease-in-out;
        transform-origin: center;
      }
      .addon-item {
        display: inline-block;
        transition: all 0.2s ease;
      }
      
      @keyframes warningPulse {
        0% { transform: scale(1);  }
        10% { transform: scale(1.08);  }
        20% { transform: scale(1.05);  }
        30% { transform: scale(1.12);  }
        40% { transform: scale(1.06);  }
        50% { transform: scale(1.1);  }
        60% { transform: scale(1.07);  }
        70% { transform: scale(1.09);  }
        80% { transform: scale(1.05);  }
        90% { transform: scale(1.02); }
        100% { transform: scale(1);  }
      }
      
      .min-qty-warning {
        display: inline-block;
        animation: warningPulse 2s ease-in-out infinite;
        transform-origin: center;
        font-weight: bold;
        color:rgb(255, 51, 0) !important;
      }
    `;
    document.head.appendChild(styleEl);

    // Add an animation reset handler to ensure animations can be replayed
    document.addEventListener("animationend", function (e) {
      if (e.target.classList.contains("addon-pulse")) {
        // Remove class after animation completes
        e.target.classList.remove("addon-pulse");
      }
    });
  }

  function addBrowserSpecificStyles() {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      /* Global styles for all browsers */
      .breakpoint-labels-container {
        position: absolute; 
        top: 0; 
        left: 0; 
        width: 100%; 
        height: 100%; 
        pointer-events: none; 
        z-index: 100 !important;
      }
      
      .breakpoint-label {
        position: absolute;
        transform: translate(-50%, -50%);
        text-align: center;
        font-family: Oswald, sans-serif;
        font-weight: bold;
        width: auto;
        white-space: nowrap;
        user-select: none;
        pointer-events: none;
        z-index: 101 !important;
      }
      
      /* Firefox-specific adjustments */
      @-moz-document url-prefix() {
        .breakpoint-label {
          will-change: transform;
          backface-visibility: hidden;
          
          z-index: 999 !important; /* Extra high z-index for Firefox */
        }
        
        .breakpoint-labels-container {
          z-index: 998 !important; /* Extra high z-index for Firefox */
        }
        
        #est_secondary_modal * {
          z-index: auto;
        }
      }
    `;
    document.head.appendChild(styleEl);
}

  function hasSignificantAddonChange(newAddonText) {
    // Only consider addons changed if the displayed text has changed
    const changed = newAddonText !== previousAddonText;

    return changed;
  }

  //
  // UTILITY FUNCTIONS
  //
  function applyStyles(el, styles) {
    for (let key in styles) {
      if (styles.hasOwnProperty(key)) {
        el.style[key] = styles[key];
      }
    }
  }

  //
  // PRIMARY ESTIMATOR SETUP
  //
  function setup_primary_estimator() {
    let primary = document.querySelector(config.primarySelector);
    if (primary) {
      if (config.modifyOriginalPrimary) {
        primary.innerHTML = config.loadingText;
        applyStyles(primary, {
          padding: "10px",
          border: "1px solid #ccc",
          margin: "10px",
          fontSize: "16px",
        });
      }
    } else if (config.recreatePrimaryIfMissing) {
      primary = document.createElement("div");
      primary.setAttribute("id", config.customClassNames.primary);
      primary.innerHTML = config.loadingText;
      if (config.hideOriginalPrimary) {
        primary.setAttribute("hidden", "true");
      }
      document.body.appendChild(primary);
    }
    return primary;
  }

  //
  // PRICING & ADDON LOGIC (Bold addon totals)
  //
  let recordedBoldOptions = {};

  function getBoldOptionByName(valueName) {
    if (
      !window.BOLD?.options?.app?.optionProducts
    ) {
      console.warn("Bold global object is not available.");
      return null;
    }
    const products = window.BOLD.options.app.optionProducts;
    for (let prod of products) {
      if (prod.optionSets && Array.isArray(prod.optionSets)) {
        for (let optionSet of prod.optionSets) {
          if (optionSet.options && Array.isArray(optionSet.options)) {
            for (let option of optionSet.options) {
              if (option.optionValues && Array.isArray(option.optionValues)) {
                for (let optVal of option.optionValues) {
                  if (
                    optVal.name &&
                    optVal.name.toLowerCase() === valueName.toLowerCase()
                  ) {
                    return optVal;
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  function getBoldOptionById(optionId) {
    if (
      !window.BOLD?.options?.app?.optionProducts
    ) {
      console.warn("Bold global object is not available.");
      return null;
    }
    const products = window.BOLD.options.app.optionProducts;
    for (let prod of products) {
      if (prod.optionSets && Array.isArray(prod.optionSets)) {
        for (let optionSet of prod.optionSets) {
          if (optionSet.options && Array.isArray(optionSet.options)) {
            for (let option of optionSet.options) {
              if (option.optionValues && Array.isArray(option.optionValues)) {
                for (let optVal of option.optionValues) {
                  if (String(optVal.id) === String(optionId)) {
                    return optVal;
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  function recordOption(optionData, isSelected) {
    // Create a composite key that combines the set name and option ID to prevent collisions
    const optionId = String(optionData.id);
    const setName = optionData.optionSet?.name || "Unknown Set";
    const compositeKey = `${setName}|${optionId}`;

    const price = optionData.price;
    let qty = optionData.qty;
    const quantityMode = optionData.quantityMode;
    let editableQty = null;

    if (quantityMode === "EDITABLE_ABSOLUTE") {
      editableQty = parseInt(qty, 10) || 1;
      qty = editableQty;
    }

    if (isSelected) {
      // Store option data with all its properties
      recordedBoldOptions[compositeKey] = {
        name: optionData.name,
        price: price,
        qty: qty,
        quantityMode: quantityMode,
        optionId: optionId, // Store the original ID for reference
        ...(quantityMode === "EDITABLE_ABSOLUTE" && {
          editableQty: editableQty,
        }),
        setName: setName,
      };
    } else {
      // Only remove this specific option if it's being deselected
      if (recordedBoldOptions.hasOwnProperty(compositeKey)) {
        delete recordedBoldOptions[compositeKey];
      }
    }

    // Debug total after update
    const total = Object.values(recordedBoldOptions)
      .filter((opt) => opt.quantityMode === "ONE_TIME_PER_PROD")
      .reduce((sum, opt) => sum + opt.price, 0);
  }

  document.addEventListener(
    "click",
    function (event) {
      const boldContainer = event.target.closest(".bold_option_value");
      if (!boldContainer) return;
      setTimeout(() => {
        const optionElement = boldContainer.querySelector(
          ".bold_option_value_element"
        );
        if (!optionElement) {
          console.warn(
            'No element with class "bold_option_value_element" found.'
          );
          return;
        }
        const isSelected =
          optionElement.classList.contains("bold_swatch_selected") ||
          optionElement.getAttribute("aria-checked") === "true";
        const titleElement = boldContainer.querySelector(
          ".bold_option_value_title"
        );
        const optionValueName = titleElement
          ? titleElement.textContent.trim()
          : "";

        const optionData = getBoldOptionByName(optionValueName);
        if (!optionData) {
          console.warn(`No Bold option data found for "${optionValueName}"`);
          return;
        }
        recordOption(optionData, isSelected);
        
        // Add this line to update the price estimate after recording the option
        delayedUpdateEstimator();
      }, 100);
    },
    false
  );

  function updateQuantity(event) {
    const target = event.target;
    if (target.matches('input[type="number"][data-option_value_key]')) {
      const newQty = parseInt(target.value, 10) || 1;
      const optionQtyKey = target.getAttribute("data-option_value_key");

      // Find the parent option set name
      const optionContainer = target.closest(".bold_option");
      const setTitle =
        optionContainer
          ?.querySelector(".bold_option_title")
          ?.textContent.trim() || "Unknown Set";

      // Find the option in our recorded options by its data-option_value_key
      let foundOption = null;
      let foundKey = null;

      for (const key in recordedBoldOptions) {
        const option = recordedBoldOptions[key];
        if (key.includes(`${setTitle}|`) && option.optionId === optionQtyKey) {
          foundOption = option;
          foundKey = key;
          break;
        }
      }

      // If not found, try to get it from Bold API
      if (!foundOption) {
        const optionData = getBoldOptionById(optionQtyKey);
        if (optionData) {
          // Add the set name to the option data
          optionData.optionSet = { name: setTitle };
          recordOption(optionData, true);

          // Now find it again with the new composite key
          const compositeKey = `${setTitle}|${optionQtyKey}`;
          foundOption = recordedBoldOptions[compositeKey];
          foundKey = compositeKey;
        } else {
          console.warn(
            `No Bold option data found for quantity key ${optionQtyKey} in set ${setTitle}.`
          );
          return;
        }
      }

      if (foundOption) {
        foundOption.qty = newQty;
        if (foundOption.quantityMode === "EDITABLE_ABSOLUTE") {
          foundOption.editableQty = newQty;
        }
      }
    }
  }
  document.addEventListener("change", updateQuantity, false);
  document.addEventListener("input", updateQuantity, false);

  // Modify the getBoldAddonTotals function to add debugging for one-time options
  function getBoldAddonTotals() {
    let perUnitAddonTotal = 0;
    let oneTimeAddonTotal = 0;
    let oneTimeOptions = [];

    // Process all options
    for (let key in recordedBoldOptions) {
      if (recordedBoldOptions.hasOwnProperty(key)) {
        let option = recordedBoldOptions[key];

        if (option.quantityMode === "DEFAULT") {
          perUnitAddonTotal += option.price;
        } else if (option.quantityMode === "ONE_TIME_PER_PROD") {
          oneTimeAddonTotal += option.price;
          oneTimeOptions.push({
            id: key,
            name: option.name,
            price: option.price,
            setName: option.setName,
          });
        } else if (option.quantityMode === "EDITABLE_ABSOLUTE") {
          let itemTotal = option.price * option.qty;
          oneTimeAddonTotal += itemTotal;
          oneTimeOptions.push({
            id: key,
            name: option.name,
            price: itemTotal,
            qty: option.qty,
            setName: option.setName,
          });
        }
      }
    }

    return {
      perUnitAddonTotal: perUnitAddonTotal,
      oneTimeAddonTotal: oneTimeAddonTotal,
      oneTimeOptions: oneTimeOptions,
    };
  }

  function getPricing(qty) {
    let basePrice = 0,
      currency = "";
    
    if (typeof determineBasePrice === "function") {
      try {
        let bp = determineBasePrice(qty);
        // Check if bp exists and is an object before accessing properties
        if (bp && typeof bp === 'object') {
          basePrice = bp.basePrice || 0;
          currency = bp.currency || "";
        } else {
          console.warn("determineBasePrice returned null or invalid value for qty:", qty);
          // Try to find pricing from other sources (fallback)
          basePrice = findFallbackPrice(qty);
        }
      } catch (error) {
        console.error("Error in determineBasePrice:", error);
        basePrice = findFallbackPrice(qty);
      }
    } else {
      console.warn("determineBasePrice function not found");
      basePrice = findFallbackPrice(qty);
    }
    
    // Normalize hat base price (assumed to be in dollars) if less than 100:
    basePrice = normalizeHatPrice(basePrice);
    
    // Rest of your existing code...
    let extrasPrice = 0;
    let boldTotals = getBoldAddonTotals();
    let perUnitAddonTotal = boldTotals.perUnitAddonTotal;
    let oneTimeAddonTotal = boldTotals.oneTimeAddonTotal;
    let computedUnitPrice = basePrice + perUnitAddonTotal;
    
    // Same issue here - need safe handling for originalData
    let originalBasePrice = basePrice; // Default fallback
    try {
      let originalData = determineBasePrice(1);
      if (originalData && typeof originalData === 'object' && originalData.basePrice) {
        originalBasePrice = normalizeHatPrice(originalData.basePrice);
      }
    } catch (error) {
      console.warn("Could not determine original base price:", error);
    }
    
    // Rest of function continues as before...
    let discountPerHat = 0;
    let discountActive = false;
    if (originalBasePrice > basePrice) {
      discountPerHat = originalBasePrice - basePrice;
      discountActive = true;
    }
    
    // Return the pricing data
    let finalUnitPrice = computedUnitPrice;
    let estimatedTotal = finalUnitPrice * qty + oneTimeAddonTotal;
    return {
      basePrice: basePrice,
      extrasPrice: extrasPrice,
      computedUnitPrice: computedUnitPrice,
      finalUnitPrice: finalUnitPrice,
      oneTimeAddonTotal: oneTimeAddonTotal,
      discountActive: discountActive,
      discountAmount: discountPerHat,
      currency: currency,
      perUnitAddonTotal: perUnitAddonTotal,
      estimatedTotal: estimatedTotal,
      originalBasePrice: originalBasePrice,
    };
  }

  // Add this new function to find a price when determineBasePrice fails
  function findFallbackPrice(qty) {
    // Try various methods to find a price
    
    // Method 1: Look for price in standard Shopify elements
    const priceElement = document.querySelector('.price__regular .price-item');
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      const price = parseFloat(priceText.replace(/[^0-9.]/g, ''));
      if (!isNaN(price)) return price;
    }
    
    // Method 2: Look for price in markup data
    const metaPrice = document.querySelector('meta[property="product:price:amount"]');
    if (metaPrice) {
      const price = parseFloat(metaPrice.getAttribute('content'));
      if (!isNaN(price)) return price;
    }
    
    // Method 3: Look for price in Shopify global variable
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const price = parseFloat(window.ShopifyAnalytics.meta.product.price / 100);
      if (!isNaN(price)) return price;
    }
    
    // Default price if all methods fail
    console.warn("All price detection methods failed, using default price");
    return 10.00; // Default safe value
  }

  function getNextTierDiscount(qty) {
    let qb = document.querySelector(config.qbGridSelector);
    if ((qb?.rows?.length ?? 0) < 2) return 0;
    let originalData = determineBasePrice(1);
    let originalBasePrice =
      originalData && originalData.basePrice
        ? normalizeHatPrice(originalData.basePrice)
        : 0;
    for (let i = 1; i < qb.rows.length; i++) {
      let row = qb.rows[i];
      let minQty = parseInt(row.cells[0].innerText.trim(), 10);
      if (qty < minQty) {
        let priceStr = row.cells[2] ? row.cells[2].innerText.trim() : "";
        let nextTierPrice = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
        if (!isNaN(nextTierPrice)) {
          return originalBasePrice - normalizeHatPrice(nextTierPrice);
        }
      }
    }
    return 0;
  }

  function getAdditionalDiscountPerItem(qty) {
    let qb = document.querySelector(config.qbGridSelector);
    if (!qb || !qb.rows || qb.rows.length < 2) return 0;
    let currentRowIndex = -1;
    for (let i = 1; i < qb.rows.length; i++) {
      let row = qb.rows[i];
      let minQty = parseInt(row.cells[0].innerText.trim(), 10);
      let maxQty = row.cells[1]
        ? parseInt(row.cells[1].innerText.trim(), 10)
        : Infinity;
      if (qty >= minQty && qty <= maxQty) {
        currentRowIndex = i;
        break;
      }
    }
    if (currentRowIndex === -1) return 0;
    if (qb.rows.length > currentRowIndex + 1) {
      let nextRow = qb.rows[currentRowIndex + 1];
      let nextPriceStr = nextRow.cells[2]
        ? nextRow.cells[2].innerText.trim()
        : "";
      let nextPrice = parseFloat(nextPriceStr.replace(/[^0-9.]/g, ""));
      if (isNaN(nextPrice)) return 0;
      let currentTierPrice = parseFloat(
        qb.rows[currentRowIndex].cells[2].innerText.replace(/[^0-9.]/g, "")
      );
      return currentTierPrice - nextPrice;
    }
    return 0;
  }

  function getDiscountForQty(qty) {
    let qb = document.querySelector(config.qbGridSelector);
    if (!qb || !qb.rows || qb.rows.length < 2) return 0;
    for (let i = 1; i < qb.rows.length; i++) {
      let row = qb.rows[i];
      let minQty = parseInt(row.cells[0].innerText.trim(), 10);
      let maxQty = row.cells[1]
        ? parseInt(row.cells[1].innerText.trim(), 10)
        : Infinity;
      if (qty >= minQty && qty <= maxQty) {
        let tierPriceStr = row.cells[2] ? row.cells[2].innerText.trim() : "";
        let tierPrice = parseFloat(tierPriceStr.replace(/[^0-9.]/g, ""));
        let originalData = determineBasePrice(1);
        let originalPrice =
          originalData && originalData.basePrice
            ? normalizeHatPrice(originalData.basePrice)
            : tierPrice;
        return isNaN(originalPrice - tierPrice) ? 0 : originalPrice - tierPrice;
      }
    }
    return 0;
  }

  //
  // MASK CANVAS SETUP (FOR MASK-FILL EFFECT)
  //
  let maskCanvas,
    maskCtx,
    maskImg,
    maskCropRect = null;
  function setup_mask_canvas(parentElement) {
    maskCanvas = document.createElement("canvas");
    maskCanvas.id = "est_mask_canvas";
    maskCanvas.style.position = "absolute";
    maskCanvas.style.left = "0";
    maskCanvas.style.top = "0";
    maskCanvas.style.width = "100%";
    maskCanvas.style.height = "100%";
    maskCanvas.style.pointerEvents = "none";
    parentElement.appendChild(maskCanvas);
    maskCtx = maskCanvas.getContext("2d");
    maskImg = new Image();

    // Add crossOrigin attribute to request CORS headers
    try {
      maskImg.crossOrigin = "anonymous";
    } catch (e) {
      console.warn("Could not set crossOrigin property on image", e);
    }

    maskImg.src = config.maskEffectConfig.maskImage;
    maskImg.onload = function () {
      try {
        maskCropRect = config.maskEffectConfig.cropMask
          ? getImageCropRect(maskImg)
          : { x: 0, y: 0, width: maskImg.width, height: maskImg.height };
        resizeMaskCanvas();
        if (maskCanvas.width > 0 && maskCanvas.height > 0) {
          updateMaskCanvas(0);
        } else {
          console.warn("maskCanvas has zero size, skipping updateMaskCanvas.");
        }
      } catch (e) {
        console.error("Error in mask canvas setup:", e);
        // Use fallback for browser that can't process the mask
        maskCropRect = {
          x: 0,
          y: 0,
          width: maskImg.width,
          height: maskImg.height,
        };
        resizeMaskCanvas();
      }
    };

    maskImg.onerror = function (e) {
      console.error("Failed to load mask image:", e);
    };
  }

  function resizeMaskCanvas() {
    if (!maskCanvas || !maskCanvas.parentElement) return;
    let rect = maskCanvas.parentElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn("Parent element has zero width/height, setting defaults.");
      rect = { width: 100, height: 100 };
    }
    maskCanvas.width = rect.width;
    maskCanvas.height = rect.height;
  }

  function getImageCropRect(image) {
    try {
      let offCanvas = document.createElement("canvas");
      offCanvas.width = image.width;
      offCanvas.height = image.height;
      let offCtx = offCanvas.getContext("2d");
      offCtx.drawImage(image, 0, 0);
      let imgData = offCtx.getImageData(0, 0, image.width, image.height);
      let data = imgData.data;
      let minX = image.width,
        minY = image.height,
        maxX = 0,
        maxY = 0;
      for (let y = 0; y < image.height; y++) {
        for (let x = 0; x < image.width; x++) {
          let index = (y * image.width + x) * 4;
          let alpha = data[index + 3];
          if (alpha > 0) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }
      return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
      };
    } catch (e) {
      console.warn(
        "Canvas security restriction in Firefox, using fallback crop rect",
        e
      );
      // Return full image dimensions as fallback when we can't access the pixel data
      return { x: 0, y: 0, width: image.width, height: image.height };
    }
  }

  function updateMaskCanvas(progress) {
    if (!maskCtx || !maskImg || !maskCropRect) return;
    try {
      maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      let offDefault = document.createElement("canvas");
      offDefault.width = maskCanvas.width;
      offDefault.height = maskCanvas.height;
      let offDefaultCtx = offDefault.getContext("2d");
      let offProgress = document.createElement("canvas");
      offProgress.width = maskCanvas.width;
      offProgress.height = maskCanvas.height;
      let offProgressCtx = offProgress.getContext("2d");
      offDefaultCtx.fillStyle = config.maskEffectConfig.defaultFillColor;
      offDefaultCtx.fillRect(0, 0, offDefault.width, offDefault.height);
      offDefaultCtx.globalCompositeOperation = "destination-in";
      offDefaultCtx.drawImage(
        maskImg,
        maskCropRect.x,
        maskCropRect.y,
        maskCropRect.width,
        maskCropRect.height,
        0,
        0,
        offDefault.width,
        offDefault.height
      );
      let fillHeight = offProgress.height * (progress / 100);
      offProgressCtx.fillStyle = config.maskEffectConfig.progressFillColor;
      offProgressCtx.fillRect(
        0,
        offProgress.height - fillHeight,
        offProgress.width,
        fillHeight
      );
      offProgressCtx.globalCompositeOperation = "destination-in";
      offProgressCtx.drawImage(
        maskImg,
        maskCropRect.x,
        maskCropRect.y,
        maskCropRect.width,
        maskCropRect.height,
        0,
        0,
        offProgress.width,
        offProgress.height
      );
      maskCtx.drawImage(offDefault, 0, 0);
      maskCtx.drawImage(offProgress, 0, 0);
    } catch (e) {
      console.warn("Error updating mask canvas:", e);
      // Fallback when canvas operations fail - just show a basic progress fill
      maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      maskCtx.fillStyle = config.maskEffectConfig.defaultFillColor;
      maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      maskCtx.fillStyle = config.maskEffectConfig.progressFillColor;
      let fillHeight = maskCanvas.height * (progress / 100);
      maskCtx.fillRect(
        0,
        maskCanvas.height - fillHeight,
        maskCanvas.width,
        fillHeight
      );
    }
  }

  //
  // BREAKPOINT & PROGRESS HELPERS
  //
  function getBreakpoints() {
    let breakpoints = [];
    
    try {
      let qb = document.querySelector(config.qbGridSelector);
      if (!qb || !qb.rows || qb.rows.length < 2) {
        // Try alternative selectors if the main one fails
        const alternativeSelectors = [
          ".bold_qb_grid",
          ".shappify_qb_grid", 
          "#boldGrid",
          ".quantity-breaks-table"
        ];
        
        for (const selector of alternativeSelectors) {
          qb = document.querySelector(selector);
          if (qb && qb.rows && qb.rows.length >= 2) {
            console.log(`Found pricing grid with alternative selector: ${selector}`);
            break;
          }
        }
      }
      
      if (!qb || !qb.rows || qb.rows.length < 2) return breakpoints;
      
      // Rest of the existing getBreakpoints logic
      for (let i = 1; i < qb.rows.length; i++) {
        let row = qb.rows[i];
        let minQty = parseInt(row.cells[0].innerText, 10);
        let maxQty = row.cells[1] ? parseInt(row.cells[1].innerText, 10) : NaN;
        if (!isNaN(minQty)) {
          breakpoints.push({ min: minQty, max: isNaN(maxQty) ? minQty : maxQty });
        }
      }
      breakpoints.sort(function (a, b) {
        return a.min - b.min;
      });
      if (
        breakpoints.length > 1 &&
        breakpoints[0].min === 1 &&
        breakpoints[1].min > 1
      ) {
        breakpoints.shift();
      }
    } catch (error) {
      console.error("Error in getBreakpoints:", error);
      // Return empty array, fallback will be applied in update_estimator
    }
    
    return breakpoints;
  }

  function getSegmentWeights(breakpoints) {
    let N = breakpoints.length;
    if (N === 0) return [];
    if (N <= 2) {
      return Array(N).fill(100 / N);
    }
    
    // Initialize weights array
    let weights = Array(N).fill(10);
    
    // First three breakpoints have the same weight
    for (let i = 0; i < Math.min(3, N); i++) {
      weights[i] = 13; // Setting the same weight for the first three
    }
    
    // Find the middle index
    let middle = Math.floor(N / 2);
    
    // Calculate segment sizes
    let firstSegmentEnd = Math.max(3, Math.floor(N / 4)); // Start after the first three
    let secondSegmentEnd = middle - (N % 2 === 0 ? 1 : 0);
    let thirdSegmentStart = middle + 1;
    let fourthSegmentStart = Math.min(N - 2, Math.ceil(N * 3/4));
    
    // Second segment: weight 13
    for (let i = firstSegmentEnd; i < secondSegmentEnd; i++) {
      weights[i] = 13;
    }
    
    // Middle segment(s): weight 13
    if (N % 2 === 0) {
      // Even number of breakpoints - two middle elements
      weights[middle - 1] = 13;
      weights[middle] = 13;
    } else {
      // Odd number of breakpoints - one middle element
      weights[middle] = 13;
    }
    
    // Fourth segment: weight 13
    for (let i = thirdSegmentStart; i < fourthSegmentStart; i++) {
      weights[i] = 13;
    }
    
    // Last two segments: weight 11
    for (let i = Math.max(0, N - 2); i < N; i++) {
      weights[i] = 10;
    }
    
    // Normalize weights to sum to 100%
    let sum = weights.reduce(function (a, b) {
      return a + b;
    }, 0);
    
    return weights.map(function (w) {
      return (w * 100) / sum;
    });
  }

  function computeSegmentedFill(qty) {
    let breakpoints = getBreakpoints();
    let weights = getSegmentWeights(breakpoints);
    let N = breakpoints.length;
    if (N === 0) return 0;
    let cumulativeWeights = [];
    let cum = 0;
    for (let i = 0; i < N; i++) {
      cum += weights[i];
      cumulativeWeights.push(cum);
    }
    if (qty < breakpoints[0].min) {
      return (qty / breakpoints[0].min) * weights[0];
    }
    for (let i = 0; i < N; i++) {
      if (qty === breakpoints[i].min) {
        return cumulativeWeights[i];
      }
    }
    for (let i = 1; i < N; i++) {
      if (qty < breakpoints[i].min) {
        let frac =
          (qty - breakpoints[i - 1].min) /
          (breakpoints[i].min - breakpoints[i - 1].min);
        return cumulativeWeights[i - 1] + frac * weights[i];
      }
    }
    return 100;
  }

  function computeProgressPercentage(qty) {
    let breakpoints = getBreakpoints();
    if (breakpoints.length === 0) return 0;
    if (qty < breakpoints[0].min) {
      return (qty / breakpoints[0].min) * 100;
    }
    let currentSegment = -1;
    for (let i = 0; i < breakpoints.length - 1; i++) {
      if (qty >= breakpoints[i].min && qty < breakpoints[i + 1].min) {
        currentSegment = i;
        break;
      }
    }
    if (currentSegment === -1) return 100;
    let segFraction =
      (qty - breakpoints[currentSegment].min) /
      (breakpoints[currentSegment + 1].min - breakpoints[currentSegment].min);
    return ((currentSegment + segFraction) / breakpoints.length) * 100;
  }

  function getAdditionalUnitsTillNextBreakpoint(qty) {
    let breakpoints = getBreakpoints();
    if (breakpoints.length === 0) return 0;
    for (const element of breakpoints) {
      if (qty < element.min) {
        return element.min - qty;
      }
    }
    return 0;
  }

  //
  // ADDON & CART HELPERS
  //
  function getSelectedAddonText() {
    let container = document.querySelector(".bold_option_set");
    if (!container) return config.addonSpinnerText;
    let selected = container.querySelectorAll("input[type='checkbox']:checked");
    if (!selected.length) return "No extras selected";
    let addonTitles = [];
    selected.forEach(function (input) {
      let label = input.closest("label");
      if (label) {
        let titleSpan = label.querySelector(".bold_option_value_title");
        addonTitles.push(
          titleSpan && titleSpan.innerText
            ? titleSpan.innerText.trim()
            : input.value
        );
      } else {
        addonTitles.push(input.value);
      }
    });
    return addonTitles.join(", ");
  }

  function getBoldOptionDataFromUI() {
    const $container = $(".bold_option_total");
    if (!$container.length) return {};
    const data = {};
    $container.find("input[data-property-name]").each(function () {
      const key = $(this).data("property-name");
      const rawValue = $(this).val();
      try {
        data[key] = JSON.parse(rawValue);
      } catch (e) {
        data[key] = rawValue;
      }
    });
    return data;
  }

  function getCartDataFromUI() {
    let itemCount = "";
    let totalPrice = parseFloat($(".cart_total_price").text());
    let currency = $(".cart_currency").text();
    let totalDiscount = parseFloat($(".cart_total_discount").text());
    let totalWeight = parseFloat($(".cart_total_weight").text());
    let note = $(".cart_note").text();

    let cartData = {
      note: note,

      cart_level_discount_applications: [],
      checkout_charge_amount: totalPrice,
      currency: currency,
      item_count: itemCount,
      items: [],
      items_subtotal_price: totalPrice,
      original_total_price: totalPrice,
      requires_shipping: true,
      total_discount: totalDiscount,
      total_price: totalPrice,
      total_weight: totalWeight,
    };

    return cartData;
  }

  //
  // EXPLOSION EFFECT (FOR NEW TIER BREAKPOINT)
  //
  let activeParticles = [];
  let animationFrameId = null;

  function getExplosionContainerForSecondary() {
    let parent = secondaryEstimatorEl.parentNode;
    if (window.getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }
    let container = parent.querySelector(".est_explosion_container");
    if (!container) {
      container = document.createElement("div");
      container.className = "est_explosion_container";
      container.style.position = "absolute";
      container.style.pointerEvents = "none";
      container.style.overflow = "hidden";
      container.style.zIndex = "10000";
      parent.appendChild(container);
    }
    return container;
  }

  function triggerExplosionEffect() {
    let explosion = config.explosionEffect;
    let container, originX, originY;
    let updateContainerPosition = null;

    // Add 1 in 1000000 special bonus chance (0.000001 probability)
    let isSpecialBonus = Math.random() < 0.1;
    // Money emojis for the fun bonus case
    let moneyEmojis = ["💰"];

    if (config.clampExplosionParticles && secondaryEstimatorEl) {
      container = getExplosionContainerForSecondary();
      updateContainerPosition = function () {
        let parentRect =
          secondaryEstimatorEl.parentNode.getBoundingClientRect();
        let secondaryRect = secondaryEstimatorEl.getBoundingClientRect();
        container.style.left = secondaryRect.left - parentRect.left + "px";
        container.style.top = secondaryRect.top - parentRect.top + "px";
        container.style.width = secondaryRect.width + "px";
        container.style.height = secondaryRect.height + "px";
      };
      updateContainerPosition();

      let originEl =
        document.getElementById("current-breakpoint-line") ||
        secondaryEstimatorEl;
      let secondaryRect = secondaryEstimatorEl.getBoundingClientRect();
      let originRect = originEl.getBoundingClientRect();
      originX = originRect.left - secondaryRect.left + originRect.width / 2;
      originY = originRect.top - secondaryRect.top + originRect.height / 2;
    } else {
      container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "0";
      container.style.top = "0";
      container.style.width = "100%";
      container.style.height = "100%";
      container.style.pointerEvents = "none";
      container.style.overflow = "hidden";
      container.style.zIndex = "10000";
      document.body.appendChild(container);
      let rect = container.getBoundingClientRect();
      originX = rect.left + 1;
      originY = rect.top + rect.height / 2;
    }

    // Log if we hit the special bonus
    if (isSpecialBonus) {
      //
    }

    // Create and add new particles
    let newParticles = [];
    let creationTime = performance.now();

    for (let i = 0; i < explosion.count; i++) {
      let p = document.createElement("div");

      // For special bonus, create a mix of both mask and emoji particles
      if (isSpecialBonus) {
        // For half the particles, use masks with bonus colors
        if (i % 2 === 0) {
          let chosen =
            explosion.colorFiltersBonus[i % explosion.colorFiltersBonus.length];

          p.innerText = moneyEmojis[i % moneyEmojis.length];
          p.style.fontSize = "24px";
        }
      }
      // For regular explosions, use the configured mode
      else if (explosion.mode === "mask") {
        let colorArray = explosion.colorFilters;
        let chosen = colorArray[Math.floor(Math.random() * colorArray.length)];

        p.style.backgroundColor = chosen.color;
        p.style.width = explosion.alternativeFontSize || "16px";
        p.style.height = explosion.alternativeFontSize || "16px";
        let maskSrc =
          explosion.alternativeMaskImage || config.maskEffectConfig.maskImage;
        p.style.webkitMaskImage = "url(" + maskSrc + ")";
        p.style.maskImage = "url(" + maskSrc + ")";
        p.style.webkitMaskSize = "contain";
        p.style.maskSize = "contain";
        p.style.webkitMaskRepeat = "no-repeat";
        p.style.maskRepeat = "no-repeat";
        p.style.webkitMaskPosition = "center";
        p.style.maskPosition = "center";

        // Apply filter if specified
        if (chosen.filter) {
          p.style.filter = chosen.filter;
        }
      } else {
        p.innerText = explosion.emojiImages[i % explosion.emojiImages.length];
        p.style.fontSize = "24px";
      }

      p.style.position = "absolute";
      p.style.transform = `translate(${originX}px, ${originY}px) rotate(0deg)`;
      p.style.opacity = 1;
      container.appendChild(p);

      let angle = Math.random() * 2 * Math.PI;
      let speed =
        explosion.initialVelocity.min +
        Math.random() *
          (explosion.initialVelocity.max - explosion.initialVelocity.min);

      // Individual particle with its creation time
      let particle = {
        el: p,
        x: originX,
        y: originY,
        vx: speed * Math.cos(angle),
        vy: speed * Math.sin(angle),
        angle: 0,
        angularVelocity: (Math.random() - 0.5) * 10,
        opacity: 1,
        container: container,
        creationTime: creationTime,
        duration: explosion.duration,
        updateContainerPosition: updateContainerPosition,
      };

      newParticles.push(particle);
    }

    // Add new particles to active particles array
    activeParticles = activeParticles.concat(newParticles);

    // Start animation loop if not already running
    if (animationFrameId === null && activeParticles.length > 0) {
      animateParticles();
    }
  }
  function animateParticles(timestamp) {
    if (activeParticles.length === 0) {
      animationFrameId = null;
      return;
    }

    let explosion = config.explosionEffect;
    let currentTime = timestamp || performance.now();
    let particlesToRemove = [];

    // Process each particle
    for (let i = 0; i < activeParticles.length; i++) {
      let p = activeParticles[i];

      // Update container position if available
      if (p.updateContainerPosition) {
        p.updateContainerPosition();
      }

      // Calculate elapsed time for this specific particle
      let elapsed = currentTime - p.creationTime;
      let lifeProgress = elapsed / p.duration;

      // If particle has exceeded its lifetime
      if (lifeProgress >= 1) {
        particlesToRemove.push(i);
        continue;
      }

      let containerWidth = p.container.offsetWidth;
      let containerHeight = p.container.offsetHeight;

      // Physics updates
      p.vy += explosion.gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= explosion.friction;
      p.vy *= explosion.friction;

      // Boundary conditions
      if (p.x < 0 || p.x > containerWidth) {
        p.vx = -p.vx;
        p.x = Math.max(0, Math.min(containerWidth, p.x));
      }
      if (p.y < 0 || p.y > containerHeight) {
        p.vy = -p.vy;
        p.y = Math.max(0, Math.min(containerHeight, p.y));
      }

      // Update rotation and opacity
      p.angle += p.angularVelocity;
      p.opacity = Math.max(0, 1 - lifeProgress);

      // Apply visual updates
      p.el.style.transform = `translate(${p.x}px, ${p.y}px) rotate(${p.angle}deg)`;
      p.el.style.opacity = p.opacity;
    }

    // Remove expired particles in reverse order to avoid index issues
    for (let i = particlesToRemove.length - 1; i >= 0; i--) {
      let index = particlesToRemove[i];
      let particle = activeParticles[index];

      // Remove the element from DOM
      if (particle.el.parentNode) {
        particle.el.parentNode.removeChild(particle.el);
      }

      // Remove from active particles array
      activeParticles.splice(index, 1);
    }

    // Continue animation if there are still active particles
    if (activeParticles.length > 0) {
      animationFrameId = requestAnimationFrame(animateParticles);
    } else {
      animationFrameId = null;
    }
  }
  //
  // CUSTOM LAYOUT RENDERING (with currency conversion & formatting)
  //
  function capitalizeWords(str) {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function renderCustomLayout(
    qty,
    breakpoints,
    additionalUnits,
    nextTierText,
    total,
    basePrice,
    extrasPrice,
    computedUnitPrice,
    finalUnitPrice,
    perUnitAddonTotal,
    oneTimeAddonTotal,
    discountActive,
    cartData,
    currentBreakpointIndex,
    originalBasePrice,
    nextTierDiscount,
    productSingular = "Hat", // Default if not provided
    productPlural = "Hats", // Default if not provided
    totalExtras = 0,
    addonChanged
  ) {
        let topRowHTML = `<div style="display: flex; justify-content: space-between; align-items: center; background-color: #333; color: #fff; padding: 0px 8px; overflow: hidden; white-space: nowrap;">
      <div style="flex: 1.15; text-align: left; min-width: 10px; overflow: hidden;"></div>
      <div style="flex: 1.4; text-align: center; font-family: Oswald, sans-serif; font-weight: bold; font-size: 14px; overflow: hidden;">
        BULK DISCOUNTS
      </div>
      <div style="flex: 1.15; text-align: right; font-family: Oswald, sans-serif; font-size: 12px; overflow: hidden; text-overflow: ellipsis; z-index: 2; display: flex; align-items: center; justify-content: flex-end; height: 100%; padding: 0 3px;">
        ${
          qty < breakpoints[0].min
            ? `<span id="min-qty-warning-text" class="min-qty-warning" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: clamp(8px, 1.2vw, 11px); padding: 0 3px; max-width: 100%;">
                ${getMinQtyWarningText(breakpoints[0].min, productPlural)}
              </span>`
            : ""
        }
      </div>
    </div>`;

    // Function to determine warning text based on screen width
    function getMinQtyWarningText(minQty, productPlural) {
      return window.innerWidth < 400 
        ? `Add at least ${minQty} ${productPlural}` 
        : `Order does not meet the minimum! (${minQty} ${productPlural})`;
    }

// Add resize listener to update text when window is resized
    setTimeout(() => {
      const warningEl = document.getElementById("min-qty-warning-text");
      if (warningEl && breakpoints.length > 0) {
        const updateWarningText = () => {
          warningEl.textContent = getMinQtyWarningText(breakpoints[0].min, productPlural);
        };
        
        window.addEventListener("resize", updateWarningText);
        
        // Store the event listener reference so it can be removed if needed
        warningEl.updateTextListener = updateWarningText;
      }
    }, 100);
    // Middle Row: Progress bar with arrow segments using SVG and dynamic clipping
    let weights = getSegmentWeights(breakpoints);
    let N = weights.length;
    let barHeight = 40; // Or get from config if needed elsewhere
    let fillWidthPercent = computeSegmentedFill(qty); // Use the calculated fill percentage

    // Helper function to calculate Y coordinate on the slanted arrow line
    function interpolateY(currentX, startX, endX, startY, endY) {
      if (endX <= startX) return startY;
      const clampedX = Math.max(startX, Math.min(currentX, endX));
      return startY + ((clampedX - startX) * (endY - startY)) / (endX - startX);
    }

    // Calculate the clip path 'd' attribute dynamically
    let clipPathD = `M 0 0 L ${fillWidthPercent} 0 L ${fillWidthPercent} ${barHeight} L 0 ${barHeight} Z`;
    let foundClipSegment = false;

    // Use a shorter arrow slant width - half the original size
    const arrowSlantWidth = 1.5; // Changed from 3 to 1.5 (half the size)

    if (N > 0) {
      let previousArrowEndPosition = 0;
      for (let i = 0; i < N; i++) {
        const breakpointQty = breakpoints[i].min;
        const breakpointPosition = computeSegmentedFill(breakpointQty);
        const arrowStartX = breakpointPosition - arrowSlantWidth;
        const arrowEndX = breakpointPosition;

        if (fillWidthPercent > arrowStartX && fillWidthPercent <= arrowEndX) {
          const yTop = interpolateY(
            fillWidthPercent,
            arrowStartX,
            arrowEndX,
            0,
            barHeight / 2
          );
          const yBottom = interpolateY(
            fillWidthPercent,
            arrowStartX,
            arrowEndX,
            barHeight,
            barHeight / 2
          );
          clipPathD = `M 0 0 L ${arrowStartX} 0 L ${fillWidthPercent} ${yTop} L ${fillWidthPercent} ${yBottom} L ${arrowStartX} ${barHeight} L 0 ${barHeight} Z`;
          foundClipSegment = true;
          break;
        }
        previousArrowEndPosition = arrowEndX;
      }
    }

    let barHTML = `<div style="position: relative; width: 100%; height: ${barHeight}px; background: ${config.progressBarColors.backgroundColor}; margin-bottom: 0px; overflow: hidden;">`;
    let segBarWidth = 1.5;
    let segArrowWidth = 0.3;
    barHTML += `<svg width="100%" height="${barHeight}" viewBox="0 0 100 ${barHeight}" preserveAspectRatio="none" style="position: absolute; left: 0; top: 0; z-index: 1;">`;

    barHTML += `<defs><clipPath id="progress-clip"><path d="${clipPathD}" /></clipPath></defs>`;
    let fillRectHTML = `<rect x="0" y="0" width="100" height="${barHeight}" fill="${config.progressBarColors.fillColor}" clip-path="url(#progress-clip)" />`;

    let blackElementsHTML = '<g style="z-index: 2;">';
    let yellowElementsHTML = '<g style="z-index: 3;">';

    blackElementsHTML += `<line x1="0" y1="0" x2="100" y2="0" stroke="#000" stroke-width="${segBarWidth}" />`;
    blackElementsHTML += `<line x1="0" y1="${barHeight}" x2="100" y2="${barHeight}" stroke="#000" stroke-width="${segBarWidth}" />`;
    blackElementsHTML += `<line x1="0" y1="0" x2="0" y2="${barHeight}" stroke="#000" stroke-width="${segArrowWidth}" />`;
    blackElementsHTML += `<line x1="100" y1="0" x2="100" y2="${barHeight}" stroke="#000" stroke-width="${segArrowWidth}" />`;

    let previousArrowStart = 0;
    if (N > 0) {
      const firstBreakpointPosition = computeSegmentedFill(breakpoints[0].min);
      const firstArrowStart = firstBreakpointPosition - arrowSlantWidth; // Updated to use the new arrowSlantWidth
      previousArrowStart = firstArrowStart;

      if (currentBreakpointIndex === 0) {
        yellowElementsHTML += `
            <g id="current-breakpoint-line-initial">
              <line x1="0" y1="0" x2="${firstArrowStart}" y2="0" stroke="#ffd700" stroke-width="${segBarWidth}" />
              <line x1="0" y1="${barHeight}" x2="${firstArrowStart}" y2="${barHeight}" stroke="#ffd700" stroke-width="${segBarWidth}" />
            </g>
          `;
      } else {
        blackElementsHTML += `
            <g id="segment-initial">
              <line x1="0" y1="0" x2="${firstArrowStart}" y2="0" stroke="#000" stroke-width="${segBarWidth}" />
              <line x1="0" y1="${barHeight}" x2="${firstArrowStart}" y2="${barHeight}" stroke="#000" stroke-width="${segBarWidth}" />
            </g>
          `;
      }
    }

    for (let i = 0; i < N; i++) {
      const breakpointPosition = computeSegmentedFill(breakpoints[i].min);
      const currentArrowStart = breakpointPosition - arrowSlantWidth; // Updated to use the new arrowSlantWidth

      if (i < N - 1) {
        if (i === currentBreakpointIndex || i === currentBreakpointIndex - 1) {
          yellowElementsHTML += `
              <g id="${
                i === currentBreakpointIndex
                  ? "current-breakpoint-line"
                  : "previous-breakpoint-line"
              }">
                <line x1="${currentArrowStart}" y1="0" x2="${breakpointPosition}" y2="${
            barHeight / 2
          }" stroke="#ffd700" stroke-width="${segArrowWidth}" />
                <line x1="${currentArrowStart}" y1="${barHeight}" x2="${breakpointPosition}" y2="${
            barHeight / 2
          }" stroke="#ffd700" stroke-width="${segArrowWidth}" />
              </g>
            `;
        } else {
          blackElementsHTML += `
              <g id="segment-${i}-arrow">
                <line x1="${currentArrowStart}" y1="0" x2="${breakpointPosition}" y2="${
            barHeight / 2
          }" stroke="#000" stroke-width="${segArrowWidth}" />
                <line x1="${currentArrowStart}" y1="${barHeight}" x2="${breakpointPosition}" y2="${
            barHeight / 2
          }" stroke="#000" stroke-width="${segArrowWidth}" />
              </g>
            `;
        }

        const nextBreakpointPosition = computeSegmentedFill(
          breakpoints[i + 1].min
        );
        const nextArrowStart = nextBreakpointPosition - arrowSlantWidth; // Updated to use the new arrowSlantWidth

        if (currentBreakpointIndex === i + 1) {
          yellowElementsHTML += `
              <g id="current-breakpoint-horiz-${i + 1}">
                <line x1="${currentArrowStart}" y1="0" x2="${nextArrowStart}" y2="0" stroke="#ffd700" stroke-width="${segBarWidth}" />
                <line x1="${currentArrowStart}" y1="${barHeight}" x2="${nextArrowStart}" y2="${barHeight}" stroke="#ffd700" stroke-width="${segBarWidth}" />
              </g>
            `;
        } else {
          blackElementsHTML += `
              <g id="segment-horiz-${i + 1}">
                <line x1="${currentArrowStart}" y1="0" x2="${nextArrowStart}" y2="0" stroke="#000" stroke-width="${segBarWidth}" />
                <line x1="${currentArrowStart}" y1="${barHeight}" x2="${nextArrowStart}" y2="${barHeight}" stroke="#000" stroke-width="${segBarWidth}" />
              </g>
            `;
        }
        previousArrowStart = nextArrowStart;
      }
    }

    if (N > 0) {
      const lastBreakpointPosition = computeSegmentedFill(
        breakpoints[N - 1].min
      );
      if (currentBreakpointIndex === N - 1) {
        yellowElementsHTML += `
            <g id="current-breakpoint-line-last-horiz">
              <line x1="${lastBreakpointPosition}" y1="0" x2="100" y2="0" stroke="#ffd700" stroke-width="${segBarWidth}" />
              <line x1="${lastBreakpointPosition}" y1="${barHeight}" x2="100" y2="${barHeight}" stroke="#ffd700" stroke-width="${segBarWidth}" />
            </g>
          `;
      } else {
        blackElementsHTML += `
            <g id="segment-last-horiz">
              <line x1="${lastBreakpointPosition}" y1="0" x2="100" y2="0" stroke="#000" stroke-width="${segBarWidth}" />
              <line x1="${lastBreakpointPosition}" y1="${barHeight}" x2="100" y2="${barHeight}" stroke="#000" stroke-width="${segBarWidth}" />
            </g>
          `;
      }
    }

   // First part remains the same
let linesHTML = blackElementsHTML + yellowElementsHTML;
barHTML += fillRectHTML;
barHTML += linesHTML;
barHTML += `</svg>`;

barHTML += `<div class="breakpoint-labels-container">`;
let previousBreakpointPosition = 0;

// Calculate segment widths and determine if we need to adjust font sizes
let segmentWidths = [];
for (let i = 0; i < N; i++) {
  const breakpointQty = breakpoints[i].min;
  const breakpointPosition = computeSegmentedFill(breakpointQty);
  const segmentWidth = i === 0 ? breakpointPosition : breakpointPosition - previousBreakpointPosition;
  segmentWidths.push({
    index: i,
    width: segmentWidth,
    position: breakpointPosition,
    center: i === 0 ? breakpointPosition / 2 : (previousBreakpointPosition + breakpointPosition) / 2
  });
  previousBreakpointPosition = breakpointPosition;
}

// Determine if we need smaller font sizes based on segment widths
const containerWidth = document.querySelectorAll('.breakpoint-labels-container')[0]?.offsetWidth || window.innerWidth;
const minWidthPerLabel = 60; // Minimum pixels needed for a reasonable label
const defaultFontSize = barHeight * 0.33;
const tightLayout = segmentWidths.some(segment => (segment.width * containerWidth / 100) < minWidthPerLabel);
const narrowSegments = segmentWidths.filter(segment => (segment.width * containerWidth / 100) < minWidthPerLabel);

// Reset previousBreakpointPosition for the main loop
previousBreakpointPosition = 0;

for (let i = 0; i < N; i++) {
  const breakpointQty = breakpoints[i].min;
  const breakpointPosition = computeSegmentedFill(breakpointQty);
  const segmentCenter = (previousBreakpointPosition + breakpointPosition) / 2;
  const segmentWidth = breakpointPosition - previousBreakpointPosition;
  
  // Style based on current tier position
  let textColor;
  let fontWeight = "bold";
  if (i < currentBreakpointIndex) {
    textColor = "#555";
    fontWeight = "normal";
  } else if (i === currentBreakpointIndex) {
    textColor = "#ffd700";
    fontWeight = "normal";
  } else if (i === currentBreakpointIndex + 1) {
    textColor = "#000";
  } else {
    textColor = "#000";
    fontWeight = "normal";
  }
  
  // Determine font size and content based on available space
  let fontSize = defaultFontSize;
  let labelText = `${breakpointQty}+`;
  
  // Set minimum font size as percentage of default font size
  const minFontSizePercent = 0.9;
  const absoluteMinFontSize = Math.max(8, defaultFontSize * minFontSizePercent);
  
  // Only apply font scaling to the last two segments
  if (i >= N - 2) {
    const isNarrowSegment = (segmentWidth * containerWidth / 100) < minWidthPerLabel;
    const availableWidth = Math.max(20, segmentWidth * containerWidth / 100);
    
    if (isNarrowSegment) {
      // Calculate appropriate size reduction based on segment width
      const reductionFactor = Math.min(0.9, availableWidth / minWidthPerLabel);
      fontSize = Math.max(absoluteMinFontSize, defaultFontSize * reductionFactor);
    }
  }
  
  // Add data attributes for the resize handler to use later
  barHTML += `
      <div class="breakpoint-label" 
           data-value="${breakpointQty}"
           data-index="${i}"
           data-segment-width="${segmentWidth}"
           data-default-size="${defaultFontSize}"
           data-is-last-two="${i >= N - 2}"
           style="left: ${segmentCenter}%; 
                  top: 50%; 
                  transform: translate(-50%, -50%);
                  font-size: ${fontSize}px;
                  color: ${textColor};
                  font-weight: ${fontWeight};">
          ${labelText}
      </div>
  `;
  
  previousBreakpointPosition = breakpointPosition;
}

barHTML += `</div>`;
barHTML += `</div>`;

// Additional resize handler will be added separately via setTimeout
// This handler will adjust labels when window size changes

    let originalTotal =
      (originalBasePrice + perUnitAddonTotal) * qty + oneTimeAddonTotal;
      let unitPriceDisplay = discountActive
      ? `<strong>${formatPrice(finalUnitPrice)}</strong>` +
        ' <span style="text-decoration: line-through; color: #999;">' +
        formatPrice(originalBasePrice) +
        "</span>"
      : `<strong>${formatPrice(finalUnitPrice)}</strong>`;
    
      let strikeThroughTotal = "";
      if (Math.abs(total - originalTotal) > 0.01) {
        strikeThroughTotal = ` <span style="text-decoration: line-through; color: #999;">${formatPrice(
          originalTotal
        )}</span>`;
      }

    // Calculate the correct total including per-quantity and one-time addons
    const correctTotal = finalUnitPrice * qty + oneTimeAddonTotal;

    let bottomRowHTML = `<div style="text-align: center; margin-top: 0px; background: #333; color: #fff; padding: 8px 6px; font-family: Oswald, sans-serif; display: flex; justify-content: center; align-items: center; overflow: hidden;">
    <div style="display: flex; align-items: center; justify-content: center; white-space: nowrap; overflow: hidden; font-size: clamp(12px, 3.5vw, 18px); letter-spacing: 0.2px; width: 100%;">
      <span style="overflow: hidden; text-overflow: ellipsis; flex-shrink: 1;">${qty} ${productPlural}</span>
      <span style="margin: 0 clamp(2px, 1vw, 10px); flex-shrink: 2;">|</span>
      <span style="overflow: hidden; text-overflow: ellipsis; flex-shrink: 1;">${unitPriceDisplay} Each</span>
      <span style="margin: 0 clamp(2px, 1vw, 10px); flex-shrink: 2;">|</span>
      <span style="overflow: hidden; text-overflow: ellipsis; flex-shrink: 1;">Total: <strong>${formatPrice(
        correctTotal
      )}</strong>${strikeThroughTotal}</span>
      <span class="info-icon" onclick="document.getElementById('order-summary-modal').style.display='block'" style="cursor: pointer; margin-left: clamp(2px, 1vw, 10px); border: 1px solid #fff; border-radius: 50%; min-width: 16px; width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center; font-size: clamp(12px, 3vw, 16px); flex-shrink: 0;">i</span>
    </div>
    </div>`;

    let modalHTML = `
    <div id="order-summary-modal" onclick="if(event.target===this)this.style.display='none'" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5); z-index: 10000; font-family: Oswald, sans-serif;">
    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 5px; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      <h2 style="margin: 0; color: #333; font-weight: bold; display: flex; align-items: center;">
      <span style="display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; border: 2px solid #333; margin-right: 10px; font-size: 16px; font-weight: normal; font-family: 'Times New Roman', serif; text-transform: none;">i</span>
      ORDER SUMMARY
      </h2>
      <span class="modal-close" onclick="document.getElementById('order-summary-modal').style.display='none'" style="cursor: pointer; font-size: 24px; font-weight: bold;">×</span>
      </div>
      <hr style="border: 0; height: 1px; background-color: #ddd; margin: 10px 0;">
      
      <h3 style="margin: 10px 0; color: #333;">PRICE ESTIMATE: <span style="font-weight: normal;">( ${qty} ${productPlural} )</span></h3>
      <hr style="border: 0; height: 1px; background-color: #ddd; margin: 10px 0;">
      
      <div style="margin: 15px 0;">
            <p style="margin: 5px 0;"><strong>Unit Price:</strong> ${formatPriceWithoutTrailingZeros(
        finalUnitPrice
      )}${
      discountActive
        ? ` <span style="text-decoration: line-through; color: #999;"> ${formatPriceWithoutTrailingZeros(
            originalBasePrice
          )}</span>`
        : ""
    }</p>
      <p style="margin: 5px 0; font-weight: bold;">Estimated Total: <span style="font-weight: normal;">${formatPriceWithoutTrailingZeros(
        correctTotal
      )} ( ${formatPriceWithoutTrailingZeros(finalUnitPrice)} x ${qty} )${
      Math.abs(total - originalTotal) > 0.01
        ? ` <span style="text-decoration: line-through; color: #999;"> ${formatPriceWithoutTrailingZeros(
            originalTotal
          )}</span>`
        : ""
    }</span></p>
      </div>
      
      ${(function () {
        // Reset totalExtras since we're calculating it fresh
        totalExtras = 0;
        
        for (const key in recordedBoldOptions) {
          const addon = recordedBoldOptions[key];
          if (addon.price !== 0) {
            if (addon.quantityMode === "EDITABLE_ABSOLUTE") {
              totalExtras += addon.price * addon.qty;
            } else {
              totalExtras += addon.price;
            }
          }
        }
        return `<h3 style="margin: 20px 0 10px 0; color: #333;">TOTAL EXTRAS : <span style="font-weight: normal;">${formatPriceWithoutTrailingZeros(totalExtras)} ( ${formatPriceWithoutTrailingZeros(perUnitAddonTotal * qty + oneTimeAddonTotal)} )</span></h3>`;
      })()}
      <hr style="border: 0; height: 1px; background-color: #ddd; margin: 10px 0;">
      
      <div style="margin: 15px 0;">
      ${(function () {
        let primaryHTML = "";
        let secondHTML = "";
        let colorMatchHTML = "";
        let keys = Object.keys(recordedBoldOptions || {});
        const combineColorMatches = true; // Set to true to combine all color matches into one entry
        
        // Rest of the function remains unchanged
        let primaryTotal = 0;
        let secondaryTotal = 0;
        let colorMatchTotal = 0;
        let totalColorMatchQty = 0;
        let baseColorMatchPrice = 0;
      
        if (keys.length === 0) {
          primaryHTML = `<p style="margin:5px 0;">No addons selected</p>`;
        } else {
          // Step 1: Group addons by their set name
          const addonsBySet = {};
          const colorMatchAddons = [];
          
          keys.forEach((key) => {
            const addon = recordedBoldOptions[key];
            
            // Check if this is a color match addon
            const isColorMatch = addon.name && (
              addon.name.toLowerCase().includes("pantone") || 
              addon.name.toLowerCase().includes("color match") ||
              addon.name.toLowerCase().includes("colour match")
            );
            
            if (isColorMatch) {
              // Add to color match collection
              colorMatchAddons.push(addon);
              
              // Track base price for combined display
              if (addon.quantityMode === "EDITABLE_ABSOLUTE" && (!baseColorMatchPrice || addon.price === baseColorMatchPrice)) {
                baseColorMatchPrice = addon.price;
                totalColorMatchQty += addon.qty || 1;
              }
              
              // Calculate total
              if (addon.quantityMode === "EDITABLE_ABSOLUTE") {
                colorMatchTotal += addon.price * (addon.qty || 1);
              } else {
                colorMatchTotal += addon.price;
              }
            } else {
              // Not a color match - add to normal groups
              const setName = addon.setName || "Unknown Set";
              if (!addonsBySet[setName]) {
                addonsBySet[setName] = [];
              }
              addonsBySet[setName].push(addon);
            }
          });
          
          // Step 2: Generate color match HTML
          if (colorMatchAddons.length > 0) {
            if (combineColorMatches && baseColorMatchPrice > 0) {
              // Combined display - one line with total quantity
                colorMatchHTML = `<p style="margin:5px 0;">+ <strong>Color Match</strong> : 
                ${formatPriceWithoutTrailingZeros(baseColorMatchPrice)} Per Color
                <span style="color:#666; font-size:0.9em;">( ${totalColorMatchQty} selected = ${formatPriceWithoutTrailingZeros(colorMatchTotal)} total )</span></p>`;
            
              } else {
              // Individual display - show each color match separately
              colorMatchAddons.forEach(addon => {
                if (addon.price !== 0) {
                  if (addon.quantityMode === "EDITABLE_ABSOLUTE") {
                    colorMatchHTML = `<p style="margin:5px 0;">+ <strong>Color Match</strong> : 
                    ${formatPriceWithoutTrailingZeros(baseColorMatchPrice)} Per Color
                    <span style="color:#666; font-size:0.9em;">( ${totalColorMatchQty} selected = ${formatPriceWithoutTrailingZeros(colorMatchTotal)} total )</span></p>`;
                  } else {
                    colorMatchHTML += `<p style="margin:5px 0;">+ <strong>${capitalizeWords(addon.name)}</strong> : 
                      ${formatPriceWithoutTrailingZeros(addon.price)}</p>`;
                  }
                }
              });
            }
          }
          
          // Step 3: Determine if each set is a secondary set
          const secondarySets = new Set();
          for (const setName in addonsBySet) {
            // Check if set name contains "2nd"
            if (setName.toLowerCase().includes("2nd")) {
              secondarySets.add(setName);
              continue;
            }
            
            // Check if any addon name in this set contains "2nd"
            for (const addon of addonsBySet[setName]) {
              if (addon.name && addon.name.toLowerCase().includes("2nd")) {
                secondarySets.add(setName);
                break;
              }
            }
          }
          
          // Step 4: Generate HTML for each addon (excluding color matches)
          for (const setName in addonsBySet) {
            const isSecondarySet = secondarySets.has(setName);
            
            for (const addon of addonsBySet[setName]) {
              if (addon.price !== 0) {
                const isOneTime =
                  addon.quantityMode === "EDITABLE_ABSOLUTE" ||
                  addon.quantityMode === "ONE_TIME_PER_PROD";
                const perText = isOneTime ? "" : ` Per ${productSingular} ( Total: ${formatPriceWithoutTrailingZeros(addon.price * qty)} )`;
                
                let addonDisplayPrice = addon.quantityMode === "EDITABLE_ABSOLUTE" ? addon.price * addon.qty : addon.price;
                let line = `<p style="margin:5px 0;">+ <strong>${capitalizeWords(addon.name)}</strong> : 
                      ${formatPriceWithoutTrailingZeros(addonDisplayPrice)}${perText}</p>`;
                
                if (isSecondarySet) {
                  secondHTML += line;
                  secondaryTotal += addonDisplayPrice;
                } else {
                  primaryHTML += line;
                  primaryTotal += addonDisplayPrice;
                }
              }
            }
          }
        }
        
        return `
          <div>
            <h4 style="margin-bottom:5px;">Primary Extras:</h4>
            ${primaryHTML || `<p style="margin:5px 0;">No primary extras selected</p>`}
          </div>
          <div style="margin-top:15px;">
            <h4 style="margin-bottom:5px;">Secondary Extras:</h4>
            ${secondHTML || `<p style="margin:5px 0;">No secondary extras selected</p>`}
          </div>
          ${colorMatchHTML ? `
          <div style="margin-top:15px; border-top: 1px solid #eee; padding-top: 10px;">
            <h4 style="margin-bottom:5px;">Color Match:</h4>
            ${colorMatchHTML}
          </div>` : ''}
        `;
      })()}
      <p style="margin: 5px 0; color: green;">+ Free Shipping + Free Setup</p>
      </div>
    </div>
    </div>`;
    return `<div style="display: flex; flex-direction: column; width: 100%;">
                ${topRowHTML}
                ${barHTML}
                ${bottomRowHTML}
                ${modalHTML}
              </div>`;
  }

  //
  // MAIN ESTIMATOR UPDATE FUNCTION (with currency conversion)
  //
  function update_estimator() {
    // Store the previous breakpoint index before we reset it
    let prevBreakpointIndex = lastBreakpointIndex;

    // Get the current quantity
    let qtyInput = document.getElementById("quantity");
    let qty = qtyInput ? parseInt(qtyInput.value, 10) : 0;
    if (isNaN(qty)) qty = 0;

    // Get pricing data
    let pricing = getPricing(qty);
    let basePrice = pricing.basePrice;
    let extrasPrice = pricing.extrasPrice;
    let computedUnitPrice = pricing.computedUnitPrice;
    let finalUnitPrice = pricing.finalUnitPrice;
    let oneTimeAddonTotal = pricing.oneTimeAddonTotal || 0;
    let discountActive = pricing.discountActive;
    let perUnitAddonTotal = pricing.perUnitAddonTotal;
    let originalBasePrice = pricing.originalBasePrice;

    // Check visibility conditions with Firefox compatibility fix
    let shouldShow = true;

    // If both visibility flags are enabled, show if EITHER condition is satisfied
    if (config.hideUntilQuantityThreshold && config.hideWithoutAddons) {
      // Fix: Force Firefox to not show by default when qty = 0
      let quantityOK = qty > 0 && qty !== 0; // Only show when qty is actually > 0
      let addonsPresent = perUnitAddonTotal > 0 || oneTimeAddonTotal > 0;

      // Show only if either condition is satisfied
      shouldShow = quantityOK || addonsPresent;
    }
    // Otherwise apply each condition independently
    else {
      // Check quantity-based visibility - Firefox compatibility fix
      if (config.hideUntilQuantityThreshold) {
        // Only show if quantity is explicitly greater than 0 (not just truthy)
        shouldShow = qty > 0;
      }

      // Check addon-based visibility
      if (
        config.hideWithoutAddons &&
        perUnitAddonTotal === 0 &&
        oneTimeAddonTotal === 0
      ) {
        shouldShow = false;
      }
    }

    // Apply visibility to secondary estimator with explicit display style
    if (secondaryEstimatorEl) {
      secondaryEstimatorEl.style.display = shouldShow ? "block" : "none";
    }

    // Calculate total (in base currency)
    let total = finalUnitPrice * qty + oneTimeAddonTotal;

    // Convert hat prices (they are normalized to cents via normalizeHatPrice earlier)
    let convertedBasePrice = convertPrice(basePrice);
    let convertedComputedUnitPrice = convertPrice(computedUnitPrice);
    let convertedFinalUnitPrice = convertPrice(finalUnitPrice);
    let convertedOriginalBasePrice = convertPrice(originalBasePrice);
    let convertedTotal = convertPrice(total);
    let convertedNextTierDiscount = convertPrice(getNextTierDiscount(qty));

    let cartData = getCartDataFromUI();
    let breakpoints = getBreakpoints();
    
    // If no breakpoints are found, provide a fallback
    if (!breakpoints || breakpoints.length === 0) {
      console.warn("No breakpoints found, using fallback breakpoint");
      breakpoints = [{ min: 12, max: 24 }]; // Fallback with reasonable default
    }
    
    let progressPercent = computeProgressPercentage(qty);
    let additionalUnits = getAdditionalUnitsTillNextBreakpoint(qty);
    let nextTierDiscount = getNextTierDiscount(qty);
    let currentBreakpointIndex = -1;

    // Find current breakpoint index - this needs to happen BEFORE we create the next tier text
    for (let i = 0; i < breakpoints.length; i++) {
      if (qty >= breakpoints[i].min) {
        currentBreakpointIndex = i;
      }
    }

    // Determine if we're below minimum quantity
    var minQtyForProduct = breakpoints.length > 0 ? breakpoints[0].min : 0;
    let isBelowMinimum = qty < minQtyForProduct;

    // Determine product type text based on isBulkHeadwear
    let productSingular = isBulkHeadwear ? "Hat" : "Item";
    let productPlural = isBulkHeadwear ? "Hats" : "Items";

    // Create next tier text based on quantity status
    let nextTierText = "";
    if (qty === 0) {
      nextTierText = "Start adding from the selection above";
    } else if (isBelowMinimum) {
      nextTierText = `<span class="min-qty-warning">Order Does Not Meet Minimum! (${minQtyForProduct} ${productPlural})</span>`;
    } else if (additionalUnits > 0) {
      nextTierText =
        "Add " +
        additionalUnits +
        " more for " +
        formatPrice(convertedNextTierDiscount) +
        " off per " +
        productSingular;
    } else {
      nextTierText = "Max tier reached";
    }

    // Trigger explosion effect if we've crossed a tier threshold - using the previous value we stored
    if (
      config.explosionEffect.enabled &&
      currentBreakpointIndex > prevBreakpointIndex
    ) {
      triggerExplosionEffect();
    }

    // Now update lastBreakpointIndex for the next call
    lastBreakpointIndex = currentBreakpointIndex;

    // Calculate addon totals for change detection
    let boldTotals = getBoldAddonTotals();
    let currentAddonCount = boldTotals.oneTimeOptions
      ? boldTotals.oneTimeOptions.length
      : 0;
      let currentAddonTotal = perUnitAddonTotal + oneTimeAddonTotal;

    // Generate the current addon text display (similar to what we'd show in the UI)
    let currentAddonText = "";

    // Build a text representation of the addons (similar to what shows in the UI)
    if (boldTotals.perUnitAddonTotal > 0) {
      currentAddonText +=
        "Addons: " +
        formatPrice(convertPrice(boldTotals.perUnitAddonTotal)) +
        " Per " +
        productSingular;
    }

    if (boldTotals.oneTimeAddonTotal > 0) {
      if (currentAddonText) {
        currentAddonText += " + ";
      }

      const oneTimeCount = boldTotals.oneTimeOptions
        ? boldTotals.oneTimeOptions.length
        : 0;

      if (oneTimeCount > 1) {
        currentAddonText +=
          "Color Match (" +
          oneTimeCount +
          "): " +
          formatPrice(convertPrice(boldTotals.oneTimeAddonTotal));
      } else {
        currentAddonText +=
          "Color Match: " +
          formatPrice(convertPrice(boldTotals.oneTimeAddonTotal));
      }
    }

    if (!currentAddonText) {
      currentAddonText = "No addons";
    }

    // Check if addon text has changed
    let addonChanged = hasSignificantAddonChange(currentAddonText);

    // Update the previous addon text for the next comparison
    previousAddonText = currentAddonText;

    // Render layout using converted values and product type text
    let layoutHTML = renderCustomLayout(
      qty,
      breakpoints,
      additionalUnits,
      nextTierText,
      convertedTotal,
      convertedBasePrice,
      extrasPrice,
      convertedComputedUnitPrice,
      convertedFinalUnitPrice,
      convertPrice(perUnitAddonTotal),
      convertPrice(oneTimeAddonTotal),
      discountActive,
      cartData,
      currentBreakpointIndex,
      convertedOriginalBasePrice,
      convertedNextTierDiscount,
      productSingular,
      productPlural,
      addonChanged
    );

    let minigameHTML = "";
    if (config.minigameEnabled && config.minigameProgressBar) {
      minigameHTML = `<div style="width: 100%; background: ${
        config.progressBarColors.backgroundColor
      }; height: ${config.progressBarHeight}; margin-top: 4px;">
                        <div style="width: ${progressPercent.toFixed(
                          1
                        )}%; height: 100%; background: ${
        config.progressBarColors.fillColor
      }; transition: width ${config.animationDuration}ms;"></div>
                      </div>`;
    }

    let combinedHTML = layoutHTML + minigameHTML;
    if (config.extraContent) {
      combinedHTML += `<div>${config.extraContent}</div>`;
    }

    if (secondaryEstimatorEl) {
      secondaryEstimatorEl.innerHTML = combinedHTML;

      // Setup modal listeners after updating the HTML
      setTimeout(function () {
        // Find the info icon and add click event
        const infoIcon = document.querySelector(".info-icon");
        if (infoIcon) {
          infoIcon.addEventListener("click", function () {
            const modal = document.getElementById("order-summary-modal");
            if (modal) modal.style.display = "block";
          });
        }

        // Find the close button and add click event
        const closeButton = document.querySelector(".modal-close");
        if (closeButton) {
          closeButton.addEventListener("click", function () {
            const modal = document.getElementById("order-summary-modal");
            if (modal) modal.style.display = "none";
          });
        }

        // Close modal when clicking outside the content
        const modal = document.getElementById("order-summary-modal");
        if (modal) {
          modal.addEventListener("click", function (event) {
            if (event.target === modal) {
              modal.style.display = "none";
            }
          });
        }
      }, 50); // Small timeout to ensure DOM is updated

      // Only apply animations if addon text actually changed
      if (addonChanged) {
        // Select ALL addon-item elements - we'll check their data attribute value directly in JS
        const addonElements =
          secondaryEstimatorEl.querySelectorAll(".addon-item");

        if (addonElements.length > 0) {
          addonElements.forEach((el) => {
            // Always add the animation class when addons change
            el.classList.add("addon-pulse");

            // Force reflow to ensure animation starts
            el.style.display = "none";
            void el.offsetWidth;
            el.style.display = "";

            // Log what we're animating for debugging
          });
        }
      }
    }
    if (config.maskEffectConfig) {
      updateMaskCanvas(progressPercent);
    }

    if (typeof config.updateCallback === "function") {
      config.updateCallback({
        qty: qty,
        unitPrice: convertedFinalUnitPrice,
        currency: Shopify.currency.active, // using Shopify.global currency active
        total: convertedTotal,
        additionalUnits: additionalUnits,
        addonInfo: getSelectedAddonText(),
      });
    }

    var minQtyForProduct = breakpoints.length > 0 ? breakpoints[0].min : 0;
    let hiddenMinField = document.getElementById("min_qty_for_product");
    if (hiddenMinField) {
      hiddenMinField.value = minQtyForProduct;
    }
  }

  //
  // SETUP: WATCHERS & OBSERVERS
  //
  function delayedUpdateEstimator() {
    setTimeout(update_estimator, 150);
  }
  function setupQuantityTracking() {
    let qtyInput = document.getElementById("quantity");
    if (qtyInput && config.trackQuantityChanges) {
      qtyInput.addEventListener("change", delayedUpdateEstimator);
      qtyInput.addEventListener("keyup", delayedUpdateEstimator);
    }
  }
  function setupAutoHideOnScroll() {
    if (!config.autoHideOnScroll) return;
    let sec = document.getElementById(config.customClassNames.secondary);
    if (!sec) return;
    let lastScrollY = window.scrollY;
    window.addEventListener("scroll", function () {
      sec.style.opacity = window.scrollY > lastScrollY ? "0" : "1";
      lastScrollY = window.scrollY;
    });
  }
  function setupPriceObserver() {
    let qb = document.querySelector(config.qbGridSelector);
    if (!qb) return;
    let observer = new MutationObserver(function (mutations) {
      setTimeout(update_estimator, 150);
    });
    observer.observe(qb, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
  function setupAddonObserver() {
    let container = document.querySelector(".bold_option_set");
    if (container) {
      container.addEventListener("change", delayedUpdateEstimator);
      container.addEventListener("input", delayedUpdateEstimator);
      let addonInputs = container.querySelectorAll("input[type='checkbox']");
      addonInputs.forEach(function (input) {
        input.addEventListener("change", delayedUpdateEstimator);
        input.addEventListener("input", delayedUpdateEstimator);
      });
      let observer = new MutationObserver(function (mutations) {
        setTimeout(update_estimator, 150);
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    } else {
      setTimeout(setupAddonObserver, 500);
    }
  }
  function setupCartObserver() {
    let cartEl = document.querySelector(".cart_total_price");
    if (cartEl) {
      let observer = new MutationObserver(function (mutations) {
        setTimeout(update_estimator, 150);
      });
      observer.observe(cartEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    } else {
      setTimeout(setupCartObserver, 500);
    }
  }
  function observeChatButton() {
    let observer = new MutationObserver(function () {
      let chatButton = document.getElementById("chat-button");
      if (chatButton) {
        updateEstimatorPosition();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  function updateEstimatorPosition() {
    if (!secondaryEstimatorEl) return;
    if (config.mode === "bubble") return;
    if (config.detectChatButton) {
      let chatButton = document.getElementById("chat-button");
      if (chatButton) {
        let chatRect = chatButton.getBoundingClientRect();
        if (chatRect.left < window.innerWidth / 2) {
          secondaryEstimatorEl.style.left = chatRect.right + 10 + "px";
          secondaryEstimatorEl.style.right = "0";
        } else {
          secondaryEstimatorEl.style.right =
            window.innerWidth - chatRect.left + 10 + "px";
          secondaryEstimatorEl.style.left = "0";
        }
      } else {
        secondaryEstimatorEl.style.left = "0";
        secondaryEstimatorEl.style.right = "0";
      }
    } else {
      secondaryEstimatorEl.style.left = "0";
      secondaryEstimatorEl.style.right = "0";
    }
  }

  //
  // SECONDARY ESTIMATOR SETUP
  //
  function setup_secondary_estimator() {
    let sec = document.getElementById(config.customClassNames.secondary);
    if (!sec) {
      sec = document.createElement("div");
      sec.setAttribute("id", config.customClassNames.secondary);
      document.body.appendChild(sec);
    }
    sec.style.position = config.useNativeSticky ? "sticky" : "fixed";
    sec.style.zIndex = "9999";
    sec.style.fontFamily = "Arial, sans-serif";
    sec.style.backgroundColor = "#ccc";
    sec.style.color = "#333";
    sec.style.padding = "0";
    applyStyles(sec, config.cssVariables);

    switch (config.position) {
      case "top":
        sec.style.top = config.placementOffset || "0px";
        sec.style.left = "0";
        sec.style.right = "0";
        break;
      case "bottom":
        sec.style.bottom = config.placementOffset || "0px";
        sec.style.left = "0";
        sec.style.right = "0";
        break;
      case "left":
        sec.style.left = config.placementOffset || "0px";
        sec.style.top = "0";
        sec.style.bottom = "0";
        break;
      case "right":
        sec.style.right = config.placementOffset || "0px";
        sec.style.top = "0";
        sec.style.bottom = "0";
        break;
      default:
        sec.style.bottom = config.placementOffset || "0px";
        sec.style.left = "0";
        sec.style.right = "0";
    }

    let layout = config.layoutConfig;
    if (
      layout.topRow &&
      layout.topRow.enabled &&
      Array.isArray(layout.topRow.columns)
    ) {
      let topRow = document.createElement("div");
      topRow.id = "est_top_row";
      topRow.style.display = "flex";
      if (layout.topRow.rowStyles) {
        applyStyles(topRow, layout.topRow.rowStyles);
      }
      layout.topRow.columns.forEach(function (col) {
        let colDiv = document.createElement("div");
        if (col.id) {
          colDiv.id = "est_" + col.id;
        }
        colDiv.style.width = col.width;
        colDiv.style.textAlign = col.align;
        if (col.columnStyles) {
          applyStyles(colDiv, col.columnStyles);
        }
        if (col.content) {
          colDiv.innerHTML = col.content;
        }
        topRow.appendChild(colDiv);
      });
      sec.appendChild(topRow);
    }
    if (
      layout.middleRow &&
      layout.middleRow.enabled &&
      Array.isArray(layout.middleRow.columns)
    ) {
      let middleRow = document.createElement("div");
      middleRow.id = "est_middle_row";
      middleRow.style.display = "flex";
      if (layout.middleRow.rowStyles) {
        applyStyles(middleRow, layout.middleRow.rowStyles);
      }
      layout.middleRow.columns.forEach(function (col) {
        let colDiv = document.createElement("div");
        if (col.id) {
          colDiv.id = "est_" + col.id;
        }
        colDiv.style.width = col.width;
        colDiv.style.textAlign = col.align;
        if (col.columnStyles) {
          applyStyles(colDiv, col.columnStyles);
        }
        if (col.id === "logo" && col.enabled) {
          let maskContainer = document.createElement("div");
          maskContainer.id = "est_mask_container";
          applyStyles(maskContainer, config.maskContainerStyles);
          colDiv.appendChild(maskContainer);
          explosionOriginEl = maskContainer;
          if (config.maskEffectConfig) {
            setup_mask_canvas(maskContainer);
          }
        }
        middleRow.appendChild(colDiv);
      });
      sec.appendChild(middleRow);
    }
    if (
      layout.bottomRow &&
      layout.bottomRow.enabled &&
      Array.isArray(layout.bottomRow.columns)
    ) {
      let bottomRow = document.createElement("div");
      bottomRow.id = "est_bottom_row";
      bottomRow.style.display = "flex";
      if (layout.bottomRow.rowStyles) {
        applyStyles(bottomRow, layout.bottomRow.rowStyles);
      }
      layout.bottomRow.columns.forEach(function (col) {
        let colDiv = document.createElement("div");
        if (col.id) {
          colDiv.id = "est_" + col.id;
        }
        colDiv.style.width = col.width;
        colDiv.style.textAlign = col.align;
        if (col.columnStyles) {
          applyStyles(colDiv, col.columnStyles);
        }
        bottomRow.appendChild(colDiv);
      });
      sec.appendChild(bottomRow);
    }
    if (config.closeButton) {
      let closeBtn = document.createElement("button");
      closeBtn.setAttribute("id", "est_secondary_close");
      closeBtn.innerHTML = config.closeButtonText;
      closeBtn.style.marginLeft = "10px";
      closeBtn.style.cursor = "pointer";
      closeBtn.addEventListener("click", function () {
        sec.style.display = "none";
      });
      sec.appendChild(closeBtn);
    }
    if (config.extraContent) {
      let extraDiv = document.createElement("div");
      extraDiv.innerHTML = config.extraContent;
      sec.appendChild(extraDiv);
    }
    document.body.appendChild(sec);
    secondaryEstimatorEl = sec;
    if (config.mode !== "bubble") {
      updateEstimatorPosition();
    }
    return sec;
  }

  //
  // INITIALIZATION ON DOM READY
  //
  document.addEventListener("DOMContentLoaded", function () {
    addAnimationStyles();
    addBrowserSpecificStyles();
    setup_primary_estimator();
    setup_secondary_estimator();
    
    // Set up critical elements with retry
    findElementWithRetry(config.qbGridSelector, function(element) {
      if (element) {
        setupPriceObserver();
      } else {
        console.warn("QB Grid not found after retries, estimator will use fallback pricing");
      }
      update_estimator(); // Update regardless of whether element was found
    });
    
    findElementWithRetry(".bold_option_set", function(element) {
      if (element) {
        setupAddonObserver();
        setupBoldOptionMonitor(); 
      } else {
        console.warn("Bold option container not found after retries");
      }
    });
    
    setupQuantityTracking();
    setupAutoHideOnScroll();
    observeChatButton();
  });

  // Auto-update integration with quick_estimate (if available)
  if (config.autoUpdate && typeof quick_estimate === "function") {
    let original_quick_estimate = quick_estimate;
    window.quick_estimate = function () {
      original_quick_estimate.apply(this, arguments);
      setTimeout(update_estimator, 150);
    };
  }

  // Expose functions for potential monkeypatching.
  window.setup_primary_estimator = setup_primary_estimator;
  window.setup_secondary_estimator = setup_secondary_estimator;
  window.update_estimator = update_estimator;
  window.setupQuantityTracking = setupQuantityTracking;
  window.computeProgressPercentage = computeProgressPercentage;
  window.getAdditionalUnitsTillNextBreakpoint =
    getAdditionalUnitsTillNextBreakpoint;
  window.setupPriceObserver = setupPriceObserver;
  window.setupAddonObserver = setupAddonObserver;
  window.getSelectedAddonText = getSelectedAddonText;
  window.setupCartObserver = setupCartObserver;

  function setupBoldOptionMonitor() {
    function scanBoldOptionState() {
      const container = document.querySelector(".bold_option_set");
      if (!container) return;

      // Track which options we've seen in this scan
      const seenCompositeKeys = {};

      // IMPORTANT: First scan for all quantity inputs - they have unique keys
      const allQuantityInputs = container.querySelectorAll(
        "input[type='number'][data-option_value_key]"
      );

      allQuantityInputs.forEach((input) => {
        const optionQtyKey = input.getAttribute("data-option_value_key");
        const qty = parseInt(input.value, 10) || 1;

        // Find the parent containers
        const optionValue = input.closest(".bold_option_value");
        const optionSet = input.closest(".bold_option");

        if (!optionValue || !optionSet) return;

        const setTitle =
          optionSet.querySelector(".bold_option_title")?.textContent.trim() ||
          "Unknown Set";
        const titleEl = optionValue.querySelector(".bold_option_value_title");
        const optionName = titleEl ? titleEl.textContent.trim() : "";

        // Check if option is selected (look for selected swatch or checked checkbox)
        const isSelected = optionValue.querySelector(
          ".bold_swatch_selected, [aria-checked='true'], input[type='checkbox']:checked"
        );

        if (isSelected || qty > 0) {
          // Try to get option data from the Bold API
          let optionData = getBoldOptionById(optionQtyKey);

          if (!optionData && optionName) {
            optionData = getBoldOptionByName(optionName);
          }

          if (optionData) {
            // Add the set information to the option data
            optionData.optionSet = { name: setTitle };

            // Update quantity data
            optionData.qty = qty;

            // Create composite key and record
            const compositeKey = `${setTitle}|${optionData.id}`;
            seenCompositeKeys[compositeKey] = true;

            // Record this option
            recordOption(optionData, true);
          }
        }
      });

      // Now check for any selected options without quantity inputs
      const optionSets = container.querySelectorAll(".bold_option");
      optionSets.forEach((optionSet) => {
        const setTitle =
          optionSet.querySelector(".bold_option_title")?.textContent.trim() ||
          "Unknown Set";

        const selectedElements = optionSet.querySelectorAll(
          ".bold_swatch_selected, [aria-checked='true'], input[type='checkbox']:checked"
        );
        selectedElements.forEach((element) => {
          const optionValue = element.closest(".bold_option_value");
          if (!optionValue) return;

          const titleEl = optionValue.querySelector(".bold_option_value_title");
          const optionName = titleEl ? titleEl.textContent.trim() : "";

          // Skip if this has already been processed via quantity inputs
          for (const key in seenCompositeKeys) {
            if (
              key.startsWith(`${setTitle}|`) &&
              recordedBoldOptions[key]?.name === optionName
            ) {
              return;
            }
          }

          let optionId = "";
          if (element.type === "checkbox") {
            optionId = element.getAttribute("data-option_value_key") || "";
          }

          let optionData;
          if (optionId) {
            optionData = getBoldOptionById(optionId);
          } else if (optionName) {
            optionData = getBoldOptionByName(optionName);
          }

          if (optionData) {
            optionData.optionSet = { name: setTitle };
            const compositeKey = `${setTitle}|${optionData.id}`;
            seenCompositeKeys[compositeKey] = true;
            recordOption(optionData, true);
          }
        });
      });

      // Remove options that aren't currently selected
      for (const key in recordedBoldOptions) {
        if (!seenCompositeKeys[key]) {
          const option = recordedBoldOptions[key];
          delete recordedBoldOptions[key];
        }
      }
      delayedUpdateEstimator();
    }

    // Initial scan
    scanBoldOptionState();

    // Set up observer for changes
    const observer = new MutationObserver(() => {
      setTimeout(scanBoldOptionState, 100); // Delay to ensure DOM is updated
    });

    const container = document.querySelector(".bold_option_set");
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "checked", "aria-checked", "value"],
      });

      // Also observe quantity input changes directly
      container.addEventListener("change", (event) => {
        if (
          event.target.matches('input[type="number"][data-option_value_key]')
        ) {
          setTimeout(scanBoldOptionState, 100);
        }
      });
    } else {
      setTimeout(setupBoldOptionMonitor, 100);
    }
  }

  // Add this near the top of the file, before any function that needs it
  function findElementWithRetry(selector, callback, maxAttempts = 20, interval = 500) {
    let attempts = 0;
    
    function attemptFind() {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Found element ${selector} after ${attempts+1} attempts`);
        callback(element);
        return true;
      } else if (++attempts < maxAttempts) {
        console.log(`Element ${selector} not found, retry ${attempts}/${maxAttempts}`);
        setTimeout(attemptFind, interval);
        return false;
      } else {
        console.warn(`Element ${selector} not found after ${maxAttempts} attempts`);
        callback(null); // Call with null to trigger fallback behavior
        return false;
      }
    }
    
    return attemptFind();
  }
})();
